import React, { useEffect } from 'react';
import { signIn, useSession } from 'next-auth/react';
import { useRouter } from 'next/router';

interface SSOLoginProps {
  /**
   * Component to show while loading/redirecting
   * Should be an overlay or loading spinner
   */
  LoadingComponent?: React.ComponentType;
  /**
   * Callback URL after successful login
   */
  callbackUrl?: string;
  /**
   * Keycloak provider name (defaults to 'keycloak')
   */
  provider?: string;
}

/**
 * Shared SSO Login component
 * Automatically redirects to Keycloak for authentication
 * Shows loading state while redirecting
 */
export const SSOLogin: React.FC<SSOLoginProps> = ({ 
  LoadingComponent,
  callbackUrl = '/',
  provider = 'keycloak'
}) => {
  const { data: session, status } = useSession();
  const router = useRouter();

  useEffect(() => {
    if (status === 'loading') return; // Still loading

    if (session) {
      // User is already logged in, redirect to callback URL
      router.push(callbackUrl);
    } else {
      // Automatically trigger Keycloak login
      signIn(provider, { callbackUrl });
    }
  }, [session, status, router, callbackUrl, provider]);

  // Show loading component or default loading state
  if (LoadingComponent) {
    return <LoadingComponent />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
        <p className="mt-4 text-gray-600">Redirecting to login...</p>
      </div>
    </div>
  );
};